import { formatDistance, subDays } from "date-fns";
import { inject, observer } from "mobx-react";
import React from "react";
import { theme } from "src/config";
import { User, Comment, Question } from "src/models";
import { AuthStore, UserStore } from "src/stores";
import { CommentReaction, DataType } from "src/types";
import styled, { css } from "styled-components";
import { Avatar, Button, Icon, Text, Tooltip } from ".";
import { sv, enGB } from "date-fns/locale";
import { useTranslation } from "react-i18next";
const Box = styled.div<any>``;

export interface ICommentEntryProps {
  comment:      Comment;
  reply?:       boolean;
  hidePrivate?: boolean;
  authStore?:   AuthStore;
  userStore?:   UserStore;
  setReply(comment: Comment): void;
}

export const CommentEntry = inject(
  "authStore",
  "userStore"
)(
  observer((props: ICommentEntryProps): JSX.Element => {
    const { comment, authStore, userStore, hidePrivate, reply, setReply } = props;
    const [showReplies, setShowReplies] = React.useState<boolean>(true);
    const { t, i18n } = useTranslation();
    const locale = i18n.language == "sv" ? sv : enGB;

    const user:    User = userStore.getItemByID(comment.user_id);
    const curUser: User = authStore.getCurUser;

    if (hidePrivate && comment.private) return null;
    return (
      <>
        <CommentListItem key={comment.id}>
          <Author>
            <Avatar
              image    = {user.getAvatar}
              size     = "medium"
              initials = {user.getInitials}
            />
            <Vote>{comment.getVote}</Vote>
          </Author>
          <CommentContainer isAnswer={comment.isAnswer}>
            <Text size="small" weight="normal">{comment.getText}</Text>
            <CommentFooter isAnswer={comment.isAnswer}>
              <CommentActionsContainer isAnswer={comment.isAnswer} reply={reply}>
                  {!comment.private && !comment.isAnswer && !reply && (
                    <CommentActions>
                      <Icon
                        size    = "xsmall"
                        cursor  = "pointer"
                        iconId  = "arrowUp"
                        onClick = {() => comment.react(CommentReaction.Up)}
                      />
                      <Icon
                        size    = "xsmall"
                        cursor  = "pointer"
                        iconId  = "arrowDown"
                        onClick = {() => comment.react(CommentReaction.Down)}
                      />
                      <Icon
                        size    = "small"
                        cursor  = "pointer"
                        iconId  = "message"
                        onClick = {() => setShowReplies(!showReplies)}
                        label   = {(!showReplies && comment.getReplies.length ) ? "" + comment.getNumReplies : ''}
                      />
                      <Icon
                        size    = "small"
                        cursor  = "pointer"
                        iconId  = "reply"
                        onClick = {() => setReply(comment)}
                      />
                    </CommentActions>
                  )}

                <CommentButtonContainer>
                  {comment.data_type === DataType.Question && 
                    <Tooltip placement="left" content={t("mark_as_question_answer")}>
                      <Button variant="icon">
                        <Icon
                          onClick = {() => ((comment.getItem as Question).set("answered", comment.isAnswer ? null : comment.id, true))}
                          size    = "large"
                          iconId  = {!comment.isAnswer ? "checkmark" : 'close'}
                          color   = {!comment.isAnswer ? theme.color.status.answered : theme.color.status.critical}
                        />
                      </Button>
                    </Tooltip>
                  }
                  {user.id === curUser.id && (
                    <Button variant="icon">
                      <Icon
                        onClick = {() => comment.remove()}
                        size    = "medium"
                        iconId  = "delete"
                        color   = {theme.color.black}
                        />
                    </Button>
                  )}
                </CommentButtonContainer>
              </CommentActionsContainer>
              <Text size="xxsmall" weight="thin">
                {user.getFullName}
                {"  "}
                {formatDistance(
                  subDays(new Date(comment.created_at), 0),
                  new Date(),
                  { addSuffix: true, locale }
                )}
              </Text>
            </CommentFooter>
          </CommentContainer>
        </CommentListItem>

        {showReplies && !comment.isAnswer && (
          <Box marginTop="0.5rem" marginLeft="1rem" maxWidth="90%">
            {comment.getReplies.map((r: Comment) => (
              <CommentReply key={r.id}>
                <CommentEntry
                  reply
                  key         = {r.id}
                  comment     = {r}
                  hidePrivate = {hidePrivate}
                  setReply    = {() => {}}
                />
              </CommentReply>
            ))}
          </Box>
        )}
      </>
    );
  })
);

const CommentButtonContainer = styled.span`
  display: flex;
`

const CommentReply = styled(Box)`
  align-items: flex-end;
  display:     flex;
  margin-left: 2.5rem;
`;

const CommentContainer = styled.div<{isAnswer: boolean}>`
  border-radius: 0.4rem;
  border:        1px solid ${props => props.isAnswer ? theme.color.green6 : theme.color.slate6};
  padding:       0.5rem 1rem;
  margin:        0rem;
  font-size:     ${theme.font.size.small};
  white-space:   pre-line;
  background:    ${props => props.isAnswer ? theme.color.green4 : theme.color.slate4};
  flex-basis:    80%;
`;

const CommentActions = styled.div`
  display:        flex;
  align-items:    center;
  gap:            1rem;
`;

const CommentFooter = styled.div<{isAnswer: boolean}>`
  border-top: 1px solid ${props => props.isAnswer ? theme.color.green6 : theme.color.slate6};
  width:      100%;
  padding-top: 0.5rem;
  margin-top: 0.875rem;
  ${(p) => p.isAnswer && css`
    flex-direction:  row-reverse;
    display:         flex;
    justify-content: space-between;
    align-items:     baseline;
  `}
  `;

const CommentListItem = styled.div`
  display:        flex;
  flex-direction: row;
  margin-bottom:  0.75rem;
`;

const CommentActionsContainer = styled(Box)<{isAnswer: boolean, reply: boolean}>`
  align-items:     center;
  flex-direction: ${(p) => p.isAnswer ? 'row-reverse' : 'row'};
  justify-content: space-between;
  display:         flex;
  ${(p) => p.reply && css`
  flex-direction:  row-reverse;
  `}
`;

const Author = styled.div`
  margin-bottom: 0.5rem;
  margin-right:  0.5rem;
  text-align:    center;
`;

const Vote = styled.span`
  color:      ${theme.color.slate11};
  font-size:  1rem;
  margin-top: 0.25rem;
`;
